// ─── COMPLEX ORDERS ───────────────────────────────────────────────────────────
// Multi-leg packages found in single-name option prints after the close. The
// detection, the floor, the labels and the highlight rule live in the backend
// (app/services/complex_orders.py); this view only reads
//   GET /api/options/complex/tickers   per-ticker summary (the landing view)
//   GET /api/options/complex           packages, one line per structure
//   GET /api/options/complex/group     every repeat of one structure
//   GET /api/options/complex/{id}      one package's legs
//
// ⚠⚠ UNAVAILABLE MEANS NOTHING ELSE. A failed or "available: false" answer shows
// the one line UNAVAILABLE_TEXT — no filters, no note, no rows kept from before.
// ⚠ Every label comes from the server and is the structure's name alone ("Call
// vertical", "Iron condor", or "Complex package, N legs"), with the rule it matched
// beneath. Nothing here says who bought or sold, or which way a trade leans: the feed
// does not carry the side of each leg (see NOTE_TEXT). The call/put mix names which
// contracts the legs are, nothing more. The only exceptions are the two hypothetical
// panel titles, PANEL_TITLES, which never claim which panel happened.
// ⚠ No price, premium or venue is shown; a missing value is left blank, never 0.
// Minimum new contracts is null when any leg's open interest is unknown — blank.
// ⚠ No top-N cutoff: both lists page through every match with the server's cursor
// ("Load more"), and the count says how many match.
import { useState, useEffect, useRef, useCallback, useId } from "react";
import { scaleLinear } from "d3";
import { C, font, backendFetch } from "./core";
import { SearchBox, TICKER_QUERY_RE, SEARCH_DEBOUNCE_MS } from "./components/SearchBox";

// ── strategy articles: THE ONE LINK LIST ─────────────────────────────────────
// Only verified entries render as links. The verified URLs were checked in a browser
// by the user. A pending entry stays here so its URL can be supplied later: set url
// and verified together. The link text is the strategy's name, never the publisher's.
export const NO_LINK = "no link, pending URL";
export const STRATEGY_LINKS = {
  vertical: { url: "https://www.investopedia.com/terms/v/verticalspread.asp", verified: true },     // same type, same expiry, two strikes
  ratio_spread: { url: "https://www.investopedia.com/terms/r/ratiospread.asp", verified: true },    // two strikes in unequal amounts
  calendar: { url: "https://www.investopedia.com/terms/c/calendarspread.asp", verified: true },     // same strike, two expiries
  diagonal: { url: "https://www.investopedia.com/terms/d/diagonalspread.asp", verified: true },     // two strikes, two expiries
  straddle: { url: "https://www.investopedia.com/terms/s/straddle.asp", verified: true },           // call and put, same strike
  strangle: { url: "https://www.investopedia.com/terms/s/strangle.asp", verified: true },           // call and put, two strikes
  risk_reversal: { url: "https://www.investopedia.com/terms/r/riskreversal.asp", verified: true },  // verified; no label links it, see SHAPE_ARTICLES
  butterfly: { url: "https://www.investopedia.com/terms/b/butterflyspread.asp", verified: true },   // three strikes, 1:2:1
  condor: { url: "https://www.investopedia.com/terms/c/condorspread.asp", verified: true },         // four strikes, one type
  iron_condor: { url: "https://www.investopedia.com/terms/i/ironcondor.asp", verified: true },      // two puts and two calls, four strikes
  iron_butterfly: { url: "https://www.investopedia.com/terms/i/ironbutterfly.asp", verified: true }, // two puts and two calls, shared middle strike
  synthetic: { url: null, verified: false, status: NO_LINK },                                       // candidate URL did not load
  broken_wing_butterfly: { url: null, verified: false, status: NO_LINK },                           // candidate URL did not load
  double_calendar: { url: null, verified: false, status: NO_LINK },                                 // candidate URL did not load
};
// Shape → the entry for each structure its label names, in the label's order; null
// is a name that stays plain, [] is a shape with no article. Every shape is listed.
// Iron condor vs iron butterfly is read from the legs (ironEntry).
export const SHAPE_ARTICLES = {
  vertical: ["vertical"],
  ratio_spread: ["ratio_spread"],
  vertical_unequal: [],
  calendar: ["calendar"],
  diagonal: ["diagonal"],
  straddle_or_synthetic: ["straddle", "synthetic"],
  strangle_or_risk_reversal: ["strangle", null],
  strangle_or_risk_reversal_unequal: [],
  butterfly: ["butterfly"],
  broken_wing_butterfly: ["broken_wing_butterfly"],
  condor: ["condor"],
  iron_condor_or_butterfly: ["iron_condor"],
  double_calendar: ["double_calendar"],
  other: [],
};
const articleUrl = (entry) => (entry && entry.verified === true && typeof entry.url === "string"
  && entry.url.startsWith("https://") ? entry.url : null);
function ironEntry(legs) {
  const puts = (legs || []).filter((l) => l.type === "put").map((l) => l.strike);
  const calls = (legs || []).filter((l) => l.type === "call").map((l) => l.strike);
  return puts.length === 2 && calls.length === 2 && Math.max(...puts) === Math.min(...calls) ? "iron_butterfly" : "iron_condor";
}
// The label split into its named structures: [{ text, url }], url null when plain. A
// label that does not split the way the shape expects stays whole and plain.
export function labelParts(d, links = STRATEGY_LINKS) {
  const plain = [{ text: d.label, url: null }];
  const known = Object.prototype.hasOwnProperty.call(SHAPE_ARTICLES, d.shape) ? SHAPE_ARTICLES[d.shape] : [];
  if (!known.length) return plain;
  const keys = d.shape === "iron_condor_or_butterfly" ? [ironEntry(d.legs)] : known;
  const names = keys.length > 1 ? String(d.label).split(" or ") : [d.label];
  if (names.length !== keys.length) return plain;
  return names.flatMap((text, i) => {
    const part = { text, url: keys[i] ? articleUrl(links[keys[i]]) : null };
    return i === 0 ? [part] : [{ text: " or ", url: null }, part];
  });
}

// ── charts ───────────────────────────────────────────────────────────────────
export const CHART_CAPTION = "Compare this with the strategy definition. Which side traded isn’t known.";
export const PANEL_TITLES = { bought: "If bought", sold: "If sold" };
export const AXIS_VALUE = "Value at expiration, before cost";
export const AXIS_LEVEL = "Underlying at expiration";
// ⚠ The app is dark-only today, but these must read on dark panels and on white: each
// is at least 3:1 against both (measured by the harness). C.amber and C.textMid are not.
export const CHART_COLORS = { line: "#3B82F6", call: "#3B82F6", put: "#D97706", ink: "#6B7C8F" };
// One expiry, and the "if bought" form is known. Everything else gets the strike map.
export const PANEL_SHAPES = ["vertical", "butterfly", "broken_wing_butterfly", "condor", "iron_condor_or_butterfly"];

function chartLegs(d) {
  return (Array.isArray(d?.legs) ? d.legs : []).filter((l) => (l.type === "call" || l.type === "put")
    && fin(l.strike) && fin(l.contracts) && l.contracts > 0 && typeof l.expiry === "string");
}
// "If bought" is the net-debit form: +1 on the legs it holds, -1 on the others. Null
// when the legs are not the structure the shape names.
export function boughtSigns(shape, legs) {
  const by = [...legs].sort((a, b) => a.strike - b.strike);
  const signed = (signs) => by.map((leg, i) => ({ ...leg, sign: signs[i] }));
  const oneType = new Set(legs.map((l) => l.type)).size === 1;
  if (shape === "vertical" && by.length === 2 && oneType) return signed(by[0].type === "call" ? [1, -1] : [-1, 1]);
  if ((shape === "butterfly" || shape === "broken_wing_butterfly") && by.length === 3 && oneType) return signed([1, -1, 1]);
  if (shape === "condor" && by.length === 4 && oneType) return signed([1, -1, -1, 1]);
  if (shape === "iron_condor_or_butterfly" && by.length === 4) {
    const puts = by.filter((l) => l.type === "put");
    const calls = by.filter((l) => l.type === "call");
    if (puts.length !== 2 || calls.length !== 2) return null;
    return [{ ...puts[0], sign: -1 }, { ...puts[1], sign: 1 }, { ...calls[0], sign: 1 }, { ...calls[1], sign: -1 }];
  }
  return null;
}
export function chartKind(d) {
  const legs = chartLegs(d);
  if (!legs.length) return null;
  if (!PANEL_SHAPES.includes(d.shape) || legs.length !== d.legs.length) return "map";
  if (new Set(legs.map((l) => l.expiry)).size !== 1) return "map";
  return boughtSigns(d.shape, legs) ? "panels" : "map";
}
export function valueAt(signed, level) {
  return signed.reduce((sum, l) => sum + l.sign * l.contracts
    * Math.max(0, l.type === "call" ? level - l.strike : l.strike - level), 0);
}
export function mirror(signed) { return signed.map((l) => ({ ...l, sign: -l.sign })); }
// The curve is straight between strikes, so the strikes and the two ends are exact.
export function curvePoints(signed) {
  const strikes = [...new Set(signed.map((l) => l.strike))].sort((a, b) => a - b);
  const low = strikes[0];
  const high = strikes[strikes.length - 1];
  const pad = Math.max((high - low) * 0.4, low * 0.05) || 1;
  return { strikes, points: [Math.max(0, low - pad), ...strikes, high + pad].map((x) => ({ x, y: valueAt(signed, x) })) };
}
export function fmtStrike(k) { return String(k); }
// The text alternative: where the curve is flat, rising or falling. No figures but strikes.
export function describeCurve({ strikes, points }) {
  const level = (y) => (Math.abs(y) < 1e-9 ? "at zero" : y > 0 ? "above zero" : "below zero");
  return points.slice(1).map((p, i) => {
    const a = points[i];
    const dy = p.y - a.y;
    const where = i === 0 ? `Below ${fmtStrike(p.x)}` : i === points.length - 2 ? `Above ${fmtStrike(a.x)}`
      : `${fmtStrike(a.x)} to ${fmtStrike(p.x)}`;
    return `${where}: ${Math.abs(dy) < 1e-9 ? `flat ${level(p.y)}` : dy > 0 ? "rising" : "falling"}.`;
  }).join(" ") + ` Strikes: ${strikes.map(fmtStrike).join(", ")}.`;
}

const PANEL = { w: 320, h: 184, l: 30, r: 10, t: 22, b: 40 };
const STAGGER_PX = 30;                  // strike labels closer than this take turns on two rows
export function Panel({ side, curve, ymax }) {
  const titleId = useId();
  const descId = useId();
  const { strikes, points } = curve;
  const xs = scaleLinear().domain([points[0].x, points[points.length - 1].x]).range([PANEL.l, PANEL.w - PANEL.r]);
  const ys = scaleLinear().domain([-ymax, ymax]).range([PANEL.h - PANEL.b, PANEL.t]);
  const ink = CHART_COLORS.ink;
  let row = 0;
  const marks = strikes.map((k, i) => {
    row = i > 0 && xs(k) - xs(strikes[i - 1]) < STAGGER_PX ? 1 - row : 0;
    return { k, x: xs(k), y: PANEL.h - PANEL.b + 11 + row * 11 };
  });
  return (
    <figure data-panel={side} style={{ flex: "1 1 260px", maxWidth: 400, minWidth: 0, margin: 0 }}>
      <figcaption id={titleId} data-panel-title style={{ color: ink, fontSize: 11, fontWeight: 700, marginBottom: 2 }}>{PANEL_TITLES[side]}</figcaption>
      <svg width="100%" viewBox={`0 0 ${PANEL.w} ${PANEL.h}`} role="img" aria-labelledby={`${titleId} ${descId}`}
        style={{ display: "block", fontFamily: font }}>
        <desc id={descId}>{`${AXIS_VALUE}. ${describeCurve(curve)}`}</desc>
        <text data-axis="value" x={PANEL.l} y={12} fontSize={10} fill={ink}>{AXIS_VALUE}</text>
        {marks.map((m) => (
          <g key={m.k} data-strike={m.k}>
            <line x1={m.x} x2={m.x} y1={PANEL.t} y2={PANEL.h - PANEL.b} stroke={ink} strokeOpacity={0.45} strokeDasharray="2 3" />
            <text data-strike-label x={m.x} y={m.y} textAnchor="middle" fontSize={10} fill={ink}>{fmtStrike(m.k)}</text>
          </g>))}
        <line data-zero x1={PANEL.l} x2={PANEL.w - PANEL.r} y1={ys(0)} y2={ys(0)} stroke={ink} />
        <text data-zero-label x={2} y={ys(0) + 3} fontSize={9} fill={ink}>zero</text>
        <path data-curve fill="none" stroke={CHART_COLORS.line} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
          d={points.map((p, i) => `${i ? "L" : "M"}${xs(p.x).toFixed(2)} ${ys(p.y).toFixed(2)}`).join(" ")} />
        <text data-axis="level" x={PANEL.w - PANEL.r} y={PANEL.h - 3} textAnchor="end" fontSize={10} fill={ink}>{AXIS_LEVEL}</text>
      </svg>
    </figure>
  );
}
function Panels({ d }) {
  const bought = boughtSigns(d.shape, chartLegs(d));
  const curve = curvePoints(bought);
  const ymax = Math.max(...curve.points.map((p) => Math.abs(p.y))) || 1;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
      <Panel side="bought" curve={curve} ymax={ymax} />
      <Panel side="sold" curve={curvePoints(mirror(bought))} ymax={ymax} />
    </div>
  );
}
const MAP = { head: 22, left: 104, col: 92, line: 18, gap: 6 };
const legWord = (l) => (l.type === "call" ? "Call" : "Put");
function LegMap({ d }) {
  const titleId = useId();
  const descId = useId();
  const legs = chartLegs(d);
  const strikes = [...new Set(legs.map((l) => l.strike))].sort((a, b) => a - b);
  const expiries = [...new Set(legs.map((l) => l.expiry))].sort();
  const at = (k, x) => legs.filter((l) => l.strike === k && l.expiry === x).sort((a, b) => (a.type < b.type ? -1 : 1));
  const tall = Math.max(...expiries.flatMap((x) => strikes.map((k) => at(k, x).length)));
  const rowH = tall * MAP.line + MAP.gap;
  const w = MAP.left + strikes.length * MAP.col;
  const h = MAP.head + expiries.length * rowH;
  const ink = CHART_COLORS.ink;
  return (
    <div style={{ overflowX: "auto" }}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-labelledby={`${titleId} ${descId}`}
        style={{ display: "block", fontFamily: font }}>
        <title id={titleId}>Strike by expiry map</title>
        <desc id={descId}>{legs.map((l) => `${legWord(l)}, strike ${fmtStrike(l.strike)}, expiry ${l.expiry}, ${nf.format(l.contracts)} contracts.`).join(" ")}</desc>
        <text x={0} y={14} fontSize={10} fill={ink}>Expiry / strike</text>
        {strikes.map((k, c) => <text key={k} data-strike-label x={MAP.left + c * MAP.col} y={14} fontSize={10} fill={ink}>{fmtStrike(k)}</text>)}
        {expiries.map((x, r) => {
          const top = MAP.head + r * rowH;
          return (
            <g key={x} data-map-expiry={x}>
              <line x1={0} x2={w} y1={top} y2={top} stroke={ink} strokeOpacity={0.3} />
              <text x={0} y={top + 14} fontSize={10} fill={ink}>{x}</text>
              {strikes.flatMap((k, c) => at(k, x).map((l, i) => (
                <g key={l.contract || `${k}${l.type}`} data-map-leg={`${x}|${k}|${l.type}`}>
                  <rect x={MAP.left + c * MAP.col} y={top + 6 + i * MAP.line} width={8} height={8} rx={2} fill={CHART_COLORS[l.type]} />
                  <text x={MAP.left + c * MAP.col + 12} y={top + 14 + i * MAP.line} fontSize={10} fill={ink}>{`${legWord(l)} ${nf.format(l.contracts)}`}</text>
                </g>)))}
            </g>);
        })}
      </svg>
    </div>
  );
}
export function PackageChart({ d }) {
  const kind = chartKind(d);
  if (!kind) return null;
  return (
    <div data-chart={kind} style={{ marginTop: 10 }}>
      {kind === "panels" ? <Panels d={d} /> : <LegMap d={d} />}
      <p data-chart-caption style={{ color: CHART_COLORS.ink, fontSize: 11, lineHeight: 1.5, margin: "6px 0 0" }}>{CHART_CAPTION}</p>
    </div>
  );
}

// ── deep link: a stable key, never the row id ────────────────────────────────
// ⚠ A forced re-run deletes and re-inserts a date's rows, so ids change. The key names
// the package by what it is: trade date, underlying, first timestamp and structure.
const LINK_PARAMS = { date: "co_date", underlying: "co_underlying", first_ts: "co_first_ts", key: "co_key" };
const LINK_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const LINK_UNDERLYING_RE = /^[A-Z0-9.-]{1,16}$/;
const LINK_KEY_RE = /^[0-9a-f]{40}$/;
export const LINK_NOT_FOUND = "package not found for this date";
export function buildDeepLink(d) {
  if (!d || !d.trade_date || !d.underlying || !d.first_ts || !d.structure_key) return "";
  const p = new URLSearchParams();
  p.set(LINK_PARAMS.date, d.trade_date);
  p.set(LINK_PARAMS.underlying, d.underlying);
  p.set(LINK_PARAMS.first_ts, d.first_ts);
  p.set(LINK_PARAMS.key, d.structure_key);
  return `/?${p.toString()}`;
}
// Null when the URL carries no package link at all; a damaged one still opens the tab
// and answers "not found".
export function parseDeepLink(search) {
  const p = new URLSearchParams(search || "");
  if (!Object.values(LINK_PARAMS).some((k) => p.has(k))) return null;
  return { date: p.get(LINK_PARAMS.date), underlying: p.get(LINK_PARAMS.underlying),
    first_ts: p.get(LINK_PARAMS.first_ts), key: p.get(LINK_PARAMS.key) };
}
function linkValid(l) {
  return !!l && LINK_DATE_RE.test(l.date || "") && LINK_UNDERLYING_RE.test(l.underlying || "")
    && LINK_KEY_RE.test(l.key || "") && Number.isFinite(new Date(l.first_ts).getTime());
}
// → { status: "ok", detail } | { status: "not_found" } | { status: "unavailable" }.
// Repeats of one structure are over a second apart (a closer one merges into the same
// package), so comparing timestamps to the millisecond picks exactly one.
export async function fetchLinked(link) {
  if (!linkValid(link)) return { status: "not_found" };
  const p = new URLSearchParams();
  p.set("date", link.date);
  p.set("underlying", link.underlying);
  p.set("structure_key", link.key);
  p.set("min_contracts", String(STORE_MIN));
  const res = await backendFetch(null, `/api/options/complex/group?${p.toString()}`);
  if (!res) return { status: "unavailable" };
  // The endpoint answers 503 for a date it does not hold, so that reads as not found.
  if (res.status === 503 || res.status === 404 || res.status === 422) return { status: "not_found" };
  if (!res.ok) return { status: "unavailable" };
  const body = await res.json();
  const want = new Date(link.first_ts).getTime();
  const hit = body && body.available === true && Array.isArray(body.items)
    ? body.items.find((x) => Number.isInteger(x?.id) && new Date(x.first_ts).getTime() === want) : null;
  if (!hit) return { status: "not_found" };
  const detail = await fetchDetail(hit.id);
  return detail ? { status: "ok", detail } : { status: "not_found" };
}

export const UNAVAILABLE_TEXT = "Complex order data unavailable";
export const NOTE_TEXT = "These are option legs that traded together as one order. The data does not say which "
  + "side bought or sold each leg, or whether the order opened or closed a position, so each package is "
  + "described only by its structure.";
export const NEW_CONTRACTS_TEXT = "Minimum new contracts: for each leg, the contracts beyond that leg's open "
  + "interest on the date shown, added up. Blank when a leg's open interest is not known.";
export const STORE_MIN = 50;            // the backend's floor (STORE_MIN_CONTRACTS)
export const DISPLAY_MIN = 250;         // the page's default (DISPLAY_MIN_CONTRACTS)
export const MIN_CONTRACT_STEPS = [50, 100, 250, 500, 1000];
export const RELATIVE_STEPS = [null, 0.1, 0.25, 0.5, 1];
export const SORTS = [
  { key: "new_contracts", label: "Minimum new contracts" },
  { key: "contracts", label: "Contracts, all legs" },
  { key: "relative_size", label: "Size vs open interest" },
  { key: "volume_share", label: "Share of day volume" },
  { key: "time", label: "Time" },
];
export const DEFAULT_SORT = "new_contracts";
export const PAGE_SIZE = 50;
export const MIX_WORDS = { calls: "Calls only", puts: "Puts only", calls_and_puts: "Calls and puts" };

const fin = (v) => typeof v === "number" && Number.isFinite(v);
const num = (v) => (fin(v) ? v : null);
const int = (v) => (Number.isInteger(v) ? v : null);

// ── data ─────────────────────────────────────────────────────────────────────
function repeatOf(r) {
  if (!r || !Number.isInteger(r.count) || r.count < 1) return null;
  return { count: r.count, contracts: int(r.contracts), first_ts: typeof r.first_ts === "string" ? r.first_ts : null,
    last_ts: typeof r.last_ts === "string" ? r.last_ts : null, highlight: r.highlight === true };
}
function packageOf(it) {
  if (!it || !Number.isInteger(it.id) || typeof it.underlying !== "string" || typeof it.label !== "string"
    || !fin(it.contracts) || it.contracts < STORE_MIN) return null;
  return { ...it, oi_ratio: num(it.oi_ratio), oi_ratio_min_leg: num(it.oi_ratio_min_leg),
    day_volume_share: num(it.day_volume_share), new_contracts: int(it.new_contracts),
    package_size: int(it.package_size), exchanges: int(it.exchanges),
    mix_label: typeof it.mix_label === "string" ? it.mix_label : "", highlight: it.highlight === true,
    oi_status: typeof it.oi_status === "string" && it.oi_status ? it.oi_status : null,
    repeat: repeatOf(it.repeat) };
}
function meta(body) {
  return {
    date: typeof body.date === "string" ? body.date : null,
    dates: Array.isArray(body.dates) ? body.dates.filter((d) => typeof d === "string") : [],
    shapes: Array.isArray(body.shapes) ? body.shapes.filter((s) => s && s.key && s.name) : [],
    mixes: Array.isArray(body.mixes) ? body.mixes.filter((s) => s && MIX_WORDS[s.key]) : [],
    next: typeof body.next_cursor === "string" ? body.next_cursor : null,
  };
}
// A usable package list answer, or null. A row missing its id, underlying, label or
// a contract count at or above the floor is dropped whole.
export function sanitize(body) {
  if (!body || body.available !== true || !Array.isArray(body.items)) return null;
  const items = body.items.map(packageOf).filter(Boolean);
  return { ...meta(body), total: fin(body.total) ? body.total : items.length,
    packages: fin(body.packages) ? body.packages : null, items };
}
export function sanitizeTickers(body) {
  if (!body || body.available !== true || !Array.isArray(body.items)) return null;
  const items = body.items.filter((t) => t && typeof t.underlying === "string" && fin(t.contracts)
    && Number.isInteger(t.packages) && t.packages > 0)
    .map((t) => ({ underlying: t.underlying, packages: t.packages, contracts: t.contracts, largest: int(t.largest),
      call_contracts: int(t.call_contracts), put_contracts: int(t.put_contracts), highlighted: int(t.highlighted) }));
  return { ...meta(body), total: fin(body.total) ? body.total : items.length, items };
}

function filterParams(f) {
  const p = new URLSearchParams();
  if (f.date) p.set("date", f.date);
  if (f.shape) p.set("shape", f.shape);
  if (f.mix && MIX_WORDS[f.mix]) p.set("mix", f.mix);
  p.set("min_contracts", String(Math.max(STORE_MIN, f.minContracts || DISPLAY_MIN)));
  if (fin(f.minRelative)) p.set("min_relative", String(f.minRelative));
  return p;
}
function searchParam(p, f) {
  const q = String(f.query || "").trim();
  if (q && TICKER_QUERY_RE.test(q)) p.set("q", q.toUpperCase());
}
export function buildQuery(f, cursor = null) {
  const p = filterParams(f);
  if (f.ticker) p.set("underlying", f.ticker);
  else searchParam(p, f);
  p.set("sort", f.sort || DEFAULT_SORT);
  p.set("limit", String(PAGE_SIZE));
  if (cursor) p.set("cursor", cursor);
  return p.toString();
}
export function buildTickerQuery(f, cursor = null) {
  const p = filterParams(f);
  searchParam(p, f);
  p.set("limit", String(PAGE_SIZE));
  if (cursor) p.set("cursor", cursor);
  return p.toString();
}
export function buildGroupQuery(f, it) {
  const p = filterParams(f);
  p.set("underlying", it.underlying);
  p.set("structure_key", it.structure_key);
  return p.toString();
}

async function getJson(path) {
  const res = await backendFetch(null, path);
  if (!res || !res.ok) return null;
  return res.json();
}
export async function fetchList(f, cursor = null) {
  return sanitize(await getJson(`/api/options/complex?${buildQuery(f, cursor)}`));
}
export async function fetchTickers(f, cursor = null) {
  return sanitizeTickers(await getJson(`/api/options/complex/tickers?${buildTickerQuery(f, cursor)}`));
}
export async function fetchGroup(f, it) {
  const b = await getJson(`/api/options/complex/group?${buildGroupQuery(f, it)}`);
  return b && b.available === true && Array.isArray(b.items) ? b.items.map(packageOf).filter(Boolean) : null;
}
export async function fetchDetail(id) {
  const b = await getJson(`/api/options/complex/${encodeURIComponent(id)}`);
  return b && b.available === true && Array.isArray(b.legs) ? b : null;
}

// ── formatting ───────────────────────────────────────────────────────────────
const nf = new Intl.NumberFormat("en-US");
export function fmtTime(iso) {
  if (typeof iso !== "string") return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour12: false, hour: "2-digit",
    minute: "2-digit", second: "2-digit" });
}
export function fmtExpiry(a, b) {
  if (typeof a !== "string") return "";
  return b && b !== a ? `${a} to ${b}` : a;
}
export function fmtRelative(it) {
  const parts = [];
  if (fin(it.oi_ratio)) parts.push(`${it.oi_ratio >= 10 ? it.oi_ratio.toFixed(0) : it.oi_ratio.toFixed(2)}× open interest`);
  if (fin(it.day_volume_share)) parts.push(`${Math.round(it.day_volume_share * 100)}% of the legs' day volume`);
  return parts.join(" · ");
}
// The server says why open interest cannot be used ("open interest unavailable for
// this date": it is stamped on or after the trade date and already includes the day).
// Shown in place of the date; the numbers it would feed stay blank, like missing OI.
export function fmtOiDate(it, long = false) {
  if (it.oi_status) return it.oi_status.charAt(0).toUpperCase() + it.oi_status.slice(1);
  if (!it.oi_date) return "";
  return long ? `Open interest as of ${it.oi_date}` : `OI as of ${it.oi_date}`;
}
export function fmtHeadline(it) {
  const s = `${nf.format(it.contracts)} contracts, all legs`;
  return fin(it.package_size) ? `${s} · ${nf.format(it.package_size)} per leg` : s;
}
export function fmtRepeat(r) {
  if (!r || r.count < 2) return "";
  const span = r.first_ts && r.last_ts ? ` · ${fmtTime(r.first_ts)} to ${fmtTime(r.last_ts)} ET` : "";
  const tot = fin(r.contracts) ? ` · ${nf.format(r.contracts)} contracts in all` : "";
  return `Repeated ${r.count} times${tot}${span}`;
}

// ── view ─────────────────────────────────────────────────────────────────────
const sel = { background: C.panel, color: C.text, border: `1px solid ${C.border}`, fontFamily: font, fontSize: 12,
  padding: "6px 8px", minHeight: 36, borderRadius: 3 };
const lbl = { color: C.textMid, fontSize: 10, letterSpacing: "0.08em", display: "block", marginBottom: 3 };

export function Filters({ f, setF, data, view }) {
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div data-filters style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
      <label><span style={lbl}>DATE</span>
        <select aria-label="Date" value={f.date || ""} onChange={set("date")} style={sel}>
          {(data?.dates || []).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </label>
      <label><span style={lbl}>STRUCTURE</span>
        <select aria-label="Structure" value={f.shape || ""} onChange={set("shape")} style={sel}>
          <option value="">All structures</option>
          {(data?.shapes || []).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
        </select>
      </label>
      <label><span style={lbl}>CALLS / PUTS</span>
        <select aria-label="Calls and puts" value={f.mix || ""} onChange={set("mix")} style={sel}>
          <option value="">All</option>
          {(data?.mixes || []).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
        </select>
      </label>
      <label><span style={lbl}>MIN CONTRACTS</span>
        <select aria-label="Minimum contracts" value={f.minContracts}
          onChange={(e) => setF((x) => ({ ...x, minContracts: Number(e.target.value) }))} style={sel}>
          {MIN_CONTRACT_STEPS.map((n) => <option key={n} value={n}>{nf.format(n)}+</option>)}
        </select>
      </label>
      <label><span style={lbl}>SIZE VS OPEN INTEREST</span>
        <select aria-label="Minimum size versus open interest" value={fin(f.minRelative) ? String(f.minRelative) : ""}
          onChange={(e) => setF((x) => ({ ...x, minRelative: e.target.value === "" ? null : Number(e.target.value) }))}
          style={sel}>
          {RELATIVE_STEPS.map((v) => <option key={String(v)} value={v === null ? "" : String(v)}>
            {v === null ? "Any" : `${v}× or more`}</option>)}
        </select>
      </label>
      {view === "packages" && (
        <label><span style={lbl}>SORT</span>
          <select aria-label="Sort" value={f.sort} onChange={set("sort")} style={sel}>
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
      )}
    </div>
  );
}

export function Row({ it, open, onToggle, member = false }) {
  const rel = fmtRelative(it);
  const marked = it.highlight || (!member && it.repeat?.highlight);
  return (
    <div data-row={it.id} data-highlight={marked ? "1" : "0"}
      style={{ borderBottom: `1px solid ${C.border}`, borderLeft: `3px solid ${marked ? C.amber : "transparent"}`,
        background: open ? C.panelAlt : "transparent" }}>
      <button onClick={() => onToggle(it.id)} aria-expanded={open}
        style={{ all: "unset", cursor: "pointer", display: "flex", flexWrap: "wrap", gap: "4px 14px", width: "100%",
          boxSizing: "border-box", padding: "8px 10px", fontFamily: font, fontSize: 12, color: C.text }}>
        <span data-f="ticker" style={{ color: C.amber, fontWeight: 700, minWidth: 56 }}>{it.underlying}</span>
        <span data-f="time" style={{ color: C.textMid, minWidth: 64 }}>{fmtTime(it.first_ts)}</span>
        <span data-f="label" style={{ flex: "1 1 240px" }}>{it.label}</span>
        <span data-f="mix" style={{ color: C.textMid, minWidth: 90 }}>{it.mix_label}</span>
        <span data-f="contracts" style={{ minWidth: 160 }}>{fmtHeadline(it)}</span>
        <span data-f="new" style={{ color: it.highlight ? C.amber : C.textMid, minWidth: 130 }}>
          {fin(it.new_contracts) ? `${nf.format(it.new_contracts)} min. new` : ""}</span>
        <span data-f="expiry" style={{ color: C.textMid, minWidth: 150 }}>{fmtExpiry(it.min_expiry, it.max_expiry)}</span>
        <span data-f="oidate" style={{ color: C.textMid, minWidth: 110 }}>{fmtOiDate(it)}</span>
        <span data-f="relative" style={{ color: C.textMid, flex: "1 1 220px" }}>{rel}</span>
      </button>
    </div>
  );
}

export function Detail({ d }) {
  if (!d) return null;
  const cell = { padding: "4px 8px", borderBottom: `1px solid ${C.border}`, textAlign: "right" };
  const share = chartKind(d) ? buildDeepLink(d) : "";
  return (
    <div data-detail={d.id} style={{ padding: "8px 10px 12px 13px", background: C.panelAlt, fontSize: 12 }}>
      <div data-f="label" style={{ color: C.text }}>
        {labelParts(d).map((p, i) => (p.url
          ? <a key={i} data-strategy-link href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: C.amber }}>{p.text}</a>
          : <span key={i}>{p.text}</span>))}
      </div>
      <div data-f="rule" style={{ color: C.textMid, marginBottom: 6 }}>Matched: {d.rule}</div>
      <div data-f="size" style={{ color: C.textMid, marginBottom: 6 }}>
        {nf.format(d.contracts)} contracts across {d.legs_count} legs
        {fin(d.package_size) ? ` (${nf.format(d.package_size)} per leg)` : ""}
        {d.slices > 1 ? `, in ${d.slices} pieces between ${fmtTime(d.first_ts)} and ${fmtTime(d.last_ts)} ET` : `, at ${fmtTime(d.first_ts)} ET`}
        {fin(d.exchanges) && d.exchanges > 1 ? `, on ${d.exchanges} exchanges` : ""}
      </div>
      <div data-f="oidate" style={{ color: C.textMid, marginBottom: 6 }}>
        {fmtOiDate(d, true)}
        {fin(d.new_contracts) ? `${fmtOiDate(d, true) ? " · " : ""}${nf.format(d.new_contracts)} minimum new contracts` : ""}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table data-legs style={{ borderCollapse: "collapse", fontFamily: font, fontSize: 12, color: C.text }}>
          <thead><tr style={{ color: C.textMid }}>
            {["Contract", "Type", "Strike", "Expiry", "Contracts", "Open interest", "Day volume"].map((h) =>
              <th key={h} style={{ ...cell, textAlign: h === "Contract" || h === "Type" ? "left" : "right", fontWeight: 400 }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {d.legs.map((l) => (
              <tr key={l.contract} data-leg={l.contract}>
                <td style={{ ...cell, textAlign: "left" }}>{l.contract}</td>
                <td style={{ ...cell, textAlign: "left" }}>{l.type === "call" ? "Call" : l.type === "put" ? "Put" : ""}</td>
                <td style={cell}>{fin(l.strike) ? l.strike : ""}</td>
                <td style={cell}>{typeof l.expiry === "string" ? l.expiry : ""}</td>
                <td data-f="legsize" style={cell}>{fin(l.contracts) ? nf.format(l.contracts) : ""}</td>
                <td data-f="oi" style={cell}>{fin(l.open_interest) ? nf.format(l.open_interest) : ""}</td>
                <td style={cell}>{fin(l.day_volume) ? nf.format(l.day_volume) : ""}</td>
              </tr>))}
          </tbody>
        </table>
      </div>
      <PackageChart d={d} />
      {share && (
        <a data-share href={share} target="_blank" rel="noopener noreferrer"
          style={{ color: C.textMid, fontSize: 11, display: "inline-block", marginTop: 6 }}>Link to this chart</a>
      )}
    </div>
  );
}

// The package a shared link names, above the list. Found by its stable key, so the
// list's own filters and pages do not have to contain it.
export function LinkedPackage({ link }) {
  const [got, setGot] = useState({ status: "loading" });
  useEffect(() => {
    let live = true;
    setGot({ status: "loading" });
    (async () => {
      let r = null;
      try { r = await fetchLinked(link); } catch { r = { status: "unavailable" }; }
      if (live) setGot(r);
    })();
    return () => { live = false; };
  }, [link]);
  const line = { padding: "8px 13px", color: C.textMid, fontSize: 12 };
  return (
    <div data-linked style={{ border: `1px solid ${C.border}`, borderRadius: 3, marginBottom: 10 }}>
      {got.status === "ok" ? <Detail d={got.detail} />
        : got.status === "not_found" ? <div data-link-not-found style={line}>{LINK_NOT_FOUND}</div>
        : got.status === "unavailable" ? <div data-detail-unavailable style={line}>Leg detail unavailable</div>
        : <div style={line}>{"Loading…"}</div>}
    </div>
  );
}

export function TickerTable({ items, onPick }) {
  const cell = { padding: "6px 8px", borderBottom: `1px solid ${C.border}`, textAlign: "right", whiteSpace: "nowrap" };
  const blank = (v) => (fin(v) ? nf.format(v) : "");
  return (
    <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 3 }}>
      <table data-tickers style={{ borderCollapse: "collapse", fontFamily: font, fontSize: 12, color: C.text, width: "100%" }}>
        <thead><tr style={{ color: C.textMid }}>
          {["Ticker", "Packages", "Contracts, all legs", "Largest", "Call contracts", "Put contracts", "Highlighted"].map((h) =>
            <th key={h} style={{ ...cell, textAlign: h === "Ticker" ? "left" : "right", fontWeight: 400 }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.underlying} data-ticker={t.underlying}>
              <td style={{ ...cell, textAlign: "left" }}>
                <button onClick={() => onPick(t.underlying)} style={{ all: "unset", cursor: "pointer", color: C.amber,
                  fontWeight: 700, minHeight: 28, display: "inline-flex", alignItems: "center" }}>{t.underlying}</button>
              </td>
              <td style={cell}>{blank(t.packages)}</td>
              <td data-f="contracts" style={cell}>{blank(t.contracts)}</td>
              <td style={cell}>{blank(t.largest)}</td>
              <td data-f="calls" style={cell}>{blank(t.call_contracts)}</td>
              <td data-f="puts" style={cell}>{blank(t.put_contracts)}</td>
              <td style={{ ...cell, color: t.highlighted ? C.amber : C.textMid }}>{blank(t.highlighted)}</td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );
}

const tabBtn = (on) => ({ ...sel, cursor: "pointer", color: on ? C.amber : C.textMid,
  borderColor: on ? C.amber : C.border });

export function ComplexOrdersTab({ link = null }) {
  const [f, setF] = useState({ query: "", date: null, shape: "", mix: "", minContracts: DISPLAY_MIN,
    minRelative: null, sort: DEFAULT_SORT, ticker: null });
  const [view, setView] = useState("tickers");
  const [st, setSt] = useState({ status: "loading", data: null, items: [] });
  const [more, setMore] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [details, setDetails] = useState({});
  const [groups, setGroups] = useState({});        // representative id → members | "loading" | "unavailable"
  const seq = useRef(0);
  const mode = f.ticker ? "packages" : view;

  const load = useCallback((ff, cursor) => (mode === "tickers" ? fetchTickers(ff, cursor) : fetchList(ff, cursor)),
    [mode]);

  // One request per filter or view change (search debounced); a newer request wins.
  useEffect(() => {
    const mine = ++seq.current;
    // Rows for the old filters are never shown under the new ones.
    setSt((s0) => ({ status: "loading", data: s0.data, items: [] }));
    setOpenId(null);
    setGroups({});
    const q = String(f.query || "").trim();
    const wait = q && !f.ticker ? SEARCH_DEBOUNCE_MS : 0;
    const t = setTimeout(async () => {
      let data = null;
      try { data = await load(f); } catch { data = null; }
      if (seq.current !== mine) return;
      if (!data) { setSt({ status: "unavailable", data: null, items: [] }); return; }
      setSt({ status: "ok", data, items: data.items, mode });
      if (!f.date && data.date) setF((x) => (x.date ? x : { ...x, date: data.date }));
    }, wait);
    return () => clearTimeout(t);
  }, [f, load, mode]);

  const loadMore = useCallback(async () => {
    if (!st.data?.next || more) return;
    setMore(true);
    const mine = seq.current;
    let data = null;
    try { data = await load(f, st.data.next); } catch { data = null; }
    setMore(false);
    if (seq.current !== mine) return;
    if (!data) { setSt({ status: "unavailable", data: null, items: [] }); return; }
    const keyOf = (x) => (mode === "tickers" ? x.underlying : x.id);
    setSt((s0) => ({ status: "ok", data: { ...s0.data, next: data.next, total: data.total }, mode: s0.mode,
      items: [...s0.items, ...data.items.filter((it) => !s0.items.some((x) => keyOf(x) === keyOf(it)))] }));
  }, [f, st, more, load, mode]);

  // key: which line is open (a member line is "m<id>", so a package that is both a
  // group's lead and one of its members never shows its legs twice).
  const toggle = useCallback(async (id, key = id) => {
    if (openId === key) { setOpenId(null); return; }
    setOpenId(key);
    if (details[id]) return;
    let d = null;
    try { d = await fetchDetail(id); } catch { d = null; }
    setDetails((x) => ({ ...x, [id]: d || "unavailable" }));
  }, [openId, details]);

  const toggleGroup = useCallback(async (it) => {
    if (groups[it.id]) { setGroups((g) => { const n = { ...g }; delete n[it.id]; return n; }); return; }
    setGroups((g) => ({ ...g, [it.id]: "loading" }));
    const mine = seq.current;
    let m = null;
    try { m = await fetchGroup(f, it); } catch { m = null; }
    if (seq.current !== mine) return;
    setGroups((g) => ({ ...g, [it.id]: m || "unavailable" }));
  }, [groups, f]);

  if (st.status === "unavailable") {
    return <div data-unavailable style={{ padding: 16, color: C.textMid, fontFamily: font, fontSize: 13 }}>{UNAVAILABLE_TEXT}</div>;
  }
  const data = st.data;
  const pick = (u) => setF((x) => ({ ...x, ticker: u }));
  const detailOf = (id, key = id) => (openId === key && (details[id] === "unavailable"
    ? <div data-detail-unavailable style={{ padding: "8px 13px", color: C.textMid, fontSize: 12 }}>Leg detail unavailable</div>
    : <Detail d={details[id]} />));
  return (
    <div style={{ padding: "12px 12px 24px", fontFamily: font, color: C.text, maxWidth: "100%", boxSizing: "border-box" }}>
      <div style={{ color: C.amber, fontWeight: 700, letterSpacing: "0.12em", fontSize: 13, marginBottom: 4 }}>COMPLEX ORDERS</div>
      <p data-note style={{ color: C.textMid, fontSize: 12, lineHeight: 1.5, margin: "0 0 4px", maxWidth: 820 }}>{NOTE_TEXT}</p>
      <p data-help style={{ color: C.textMid, fontSize: 11, lineHeight: 1.5, margin: "0 0 10px", maxWidth: 820 }}>{NEW_CONTRACTS_TEXT}</p>
      {link && <LinkedPackage link={link} />}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        {f.ticker ? (
          <button data-back onClick={() => setF((x) => ({ ...x, ticker: null }))} style={tabBtn(false)}>← All tickers</button>
        ) : (
          <>
            <button data-view="tickers" aria-pressed={view === "tickers"} onClick={() => setView("tickers")} style={tabBtn(view === "tickers")}>By ticker</button>
            <button data-view="packages" aria-pressed={view === "packages"} onClick={() => setView("packages")} style={tabBtn(view === "packages")}>All packages</button>
          </>
        )}
      </div>
      {f.ticker
        ? <div data-ticker-title style={{ color: C.amber, fontWeight: 700, margin: "4px 0 8px" }}>{f.ticker}</div>
        : <SearchBox value={f.query} onChange={(v) => setF((x) => ({ ...x, query: v }))} placeholder="Search by ticker..." />}
      <Filters f={f} setF={setF} data={data} view={mode} />
      {/* ⚠ st.mode !== mode: the view just switched and the effect has not cleared
          the other view's items yet — never render ticker rows as packages. */}
      {st.status === "loading" || !data || st.mode !== mode ? (
        <div style={{ color: C.textMid, fontSize: 12 }}>Loading…</div>
      ) : (
        <>
          <div data-count style={{ color: C.textMid, fontSize: 11, marginBottom: 6 }}>
            {mode === "tickers"
              ? `${nf.format(st.items.length)} of ${nf.format(data.total)} tickers`
              : `${nf.format(st.items.length)} of ${nf.format(data.total)} structures`
                + (fin(data.packages) ? ` (${nf.format(data.packages)} packages)` : "")}
            {data.date ? ` · ${data.date}` : ""}
          </div>
          {st.items.length === 0 ? (
            <div data-empty style={{ color: C.textMid, fontSize: 12, padding: "8px 0" }}>
              {mode === "tickers" ? "No tickers match these filters." : "No packages match these filters."}</div>
          ) : mode === "tickers" ? (
            <TickerTable items={st.items} onPick={pick} />
          ) : (
            <div data-list style={{ border: `1px solid ${C.border}`, borderRadius: 3 }}>
              {st.items.map((it) => (
                <div key={it.id}>
                  <Row it={it} open={openId === it.id} onToggle={toggle} />
                  {detailOf(it.id)}
                  {it.repeat && it.repeat.count > 1 && (
                    <button data-repeat={it.id} aria-expanded={!!groups[it.id]} onClick={() => toggleGroup(it)}
                      style={{ all: "unset", cursor: "pointer", display: "block", padding: "4px 13px 8px", fontSize: 11,
                        color: C.textMid, minHeight: 24, boxSizing: "border-box" }}>
                      {groups[it.id] ? "▾ " : "▸ "}{fmtRepeat(it.repeat)}
                    </button>
                  )}
                  {Array.isArray(groups[it.id]) && (
                    <div data-members={it.id} style={{ marginLeft: 13, borderLeft: `1px solid ${C.border}` }}>
                      {groups[it.id].map((m) => (
                        <div key={m.id}>
                          <Row it={m} member open={openId === "m" + m.id} onToggle={(id) => toggle(id, "m" + id)} />
                          {detailOf(m.id, "m" + m.id)}
                        </div>))}
                    </div>
                  )}
                  {groups[it.id] === "unavailable" && (
                    <div data-members-unavailable style={{ padding: "4px 13px 8px", color: C.textMid, fontSize: 11 }}>Repeats unavailable</div>
                  )}
                </div>))}
            </div>
          )}
          {data.next && (
            <button data-more onClick={loadMore} disabled={more}
              style={{ ...sel, marginTop: 10, cursor: "pointer", color: C.amber }}>
              {more ? "Loading…" : `Load more (${nf.format(data.total - st.items.length)} left)`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default ComplexOrdersTab;
